import { useEffect, useState } from "react";

import { getToken, removeTokenPair } from "apiCommandsExecutor/token";
import { getAccount } from "services/account/rest";
import { AccountDto } from "services/account/restModels";

export interface UseSessionResult {
    account: AccountDto | null;
    loading: boolean;
    // Положить в сессию то, что вернула мутация аккаунта, вместо повторного запроса.
    setAccount: (account: AccountDto) => void;
    refresh: () => Promise<AccountDto | null>;
    signOut: () => void;
}

/*
* Сессия кабинета: кто вошёл и в каком заведении. Живёт на уровне модуля — спрашивают её и
* каркас приложения, и отдельные фичи, а за время сессии она не меняется.
*
* ⚠️ Кеш обязан быть с подписчиками, а не просто с переменной модуля (как в useConstants):
* сессию не только читают, но и сбрасывают из другого места. Выход из аккаунта происходит на
* странице аккаунта, а увести на форму входа должен SecurityLayout — без рассылки он о сбросе
* не узнаёт, остаётся смонтированным и показывает ошибки загрузки на каждом экране.
*
* Отдельного состояния «не авторизован» нет: account === null после загрузки и есть оно.
* Ручка /account закрыта авторизацией, поэтому просроченный или чужой токен даёт 401,
* restGet возвращает null, и SecurityLayout уводит на вход.
* */
let session: AccountDto | null = null;
let isLoaded = false;
let inFlight: Promise<AccountDto | null> | null = null;

/*
* Поколение сессии: растёт на каждый вход, выход и принудительное обновление. Ответ, стартовавший
* до этого момента, отбрасывается.
* ⚠️ Без счётчика ломались два сценария. (1) Пользователь пришёл на форму входа с протухшим
* токеном, запрос за аккаунтом ещё летит; он успевает войти, refresh() возвращает **тот же**
* промис (он ещё в inFlight), тот приходит 401 — и вход откатывается обратно на форму.
* (2) Выход во время загрузки: ответ приходит после removeTokenPair и возвращает кабинет
* пользователю, у которого уже нет токена.
* */
let generation = 0;

const listeners = new Set<(account: AccountDto | null) => void>();

const publish = (account: AccountDto | null) => {
    session = account;
    isLoaded = true;
    listeners.forEach((listener) => listener(account));
};

const loadSession = (): Promise<AccountDto | null> => {
    if (isLoaded) return Promise.resolve(session);

    if (!inFlight) {
        const startedAt = generation;

        // Без токена ходить некуда: ручка закрыта авторизацией и ответит 401.
        inFlight = (getToken() ? getAccount() : Promise.resolve(null))
            .catch(() => null)
            .then((account) => {
                if (startedAt !== generation) return session;

                inFlight = null;
                publish(account);

                return account;
            });
    }

    return inFlight;
};

const invalidateSession = () => {
    generation += 1;
    inFlight = null;
};

export const useSession = (): UseSessionResult => {
    const [account, setAccount] = useState<AccountDto | null>(session);
    const [loading, setLoading] = useState<boolean>(!isLoaded);

    useEffect(() => {
        const listener = (next: AccountDto | null) => {
            setAccount(next);
            setLoading(false);
        };

        listeners.add(listener);

        if (isLoaded) {
            setAccount(session);
            setLoading(false);
        } else {
            setLoading(true);
            loadSession();
        }

        return () => {
            listeners.delete(listener);
        };
    }, []);

    return {
        account,
        loading,
        setAccount: publish,
        refresh: () => {
            invalidateSession();
            isLoaded = false;

            return loadSession();
        },
        signOut: () => {
            invalidateSession();
            removeTokenPair();
            // Состояние известно и без запроса — сессии нет; рассылка уводит каркас на вход.
            publish(null);
        },
    };
};
